import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Video, Calendar, Clock, CheckCircle, BarChart2, Plus, Book, AlertCircle, Check } from 'lucide-react';
import { DERS_RENKLERI, useProgramContext } from '../contexts/ProgramContext';
import { ScheduleModal } from '../components/ScheduleModal';
// Güncellenmiş Assignment interface
interface Assignment {
  id: number;
  title: string;
  subject: string;
  type: 'test' | 'video' | 'physical-book';
  dueDate: string;
  daysLeft: number;
  status: 'pending' | 'waiting-approval' | 'completed';
  description: string;
  source: 'egitim-pilotu' | 'institution';
  // Fiziki kitap
  bookName?: string;
  pageRange?: string;
  // Kurum ödevi
  institutionName?: string;
  teacherName?: string;
  // Tamamlanma
  markedAsComplete?: boolean;
  markedDate?: string;
  // Öğretmen onayı
  teacherApproved?: boolean;
  approvalDate?: string;
  teacherNote?: string;
  // Sonuç
  result?: {
    score?: number;
    total?: number;
    description: string;
  };
  targetPath: string;
}
// Mock Data - Eğitim Pilotu Ödevleri
const EGITIM_PILOTU_ASSIGNMENTS: Assignment[] = [{
  id: 1,
  title: 'Üslü Sayılar Tekrar Testi',
  subject: 'MATEMATİK',
  type: 'test',
  dueDate: '2025-10-25',
  daysLeft: 2,
  status: 'pending',
  description: 'Üslü sayılar konusunu pekiştirmek için hazırlanmış 15 soruluk test.',
  source: 'egitim-pilotu',
  targetPath: '/soru-cozum'
}, {
  id: 2,
  title: 'Cümlenin Öğeleri Konu Anlatımı',
  subject: 'TÜRKÇE',
  type: 'video',
  dueDate: '2025-10-28',
  daysLeft: 5,
  status: 'pending',
  description: 'Cümlenin öğeleri konusunu anlatan 25 dakikalık video.',
  source: 'egitim-pilotu',
  targetPath: '/konu-anlatim'
}, {
  id: 3,
  title: 'Limit Matematik Test Kitabı',
  subject: 'MATEMATİK',
  type: 'physical-book',
  dueDate: '2025-10-27',
  daysLeft: 4,
  status: 'pending',
  description: 'Üslü sayılar ve köklü sayılar konularını içeren test çalışması.',
  source: 'egitim-pilotu',
  bookName: 'Limit Matematik Test Kitabı',
  pageRange: 'Sayfa 45-52',
  targetPath: '#'
}, {
  id: 4,
  title: 'Hücre Bölünmesi Değerlendirme Sınavı',
  subject: 'FEN BİLİMLERİ',
  type: 'test',
  dueDate: '2025-10-18',
  daysLeft: -5,
  status: 'completed',
  description: 'Hücre bölünmesi konusunu kapsayan değerlendirme sınavı.',
  source: 'egitim-pilotu',
  result: {
    score: 18,
    total: 20,
    description: '20 sorudan 18 doğru'
  },
  targetPath: '/sinav-analizi'
}];
// Mock Data - Kurum Ödevleri
const INSTITUTION_ASSIGNMENTS: Assignment[] = [{
  id: 101,
  title: 'Osmanlı Tarihi Test Çalışması',
  subject: 'T.C. İNKILAP TARİHİ VE ATATÜRKÇÜLÜK',
  type: 'test',
  dueDate: '2025-10-26',
  daysLeft: 3,
  status: 'pending',
  description: 'Osmanlı Devleti kuruluş ve yükseliş dönemleri test çalışması.',
  source: 'institution',
  institutionName: 'Bahçeşehir Koleji',
  teacherName: 'Mehmet Kaya',
  targetPath: '/soru-cozum'
}, {
  id: 102,
  title: 'İngilizce Diyaloglar Video',
  subject: 'İNGİLİZCE',
  type: 'video',
  dueDate: '2025-10-29',
  daysLeft: 6,
  status: 'waiting-approval',
  description: 'Günlük konuşma diyaloglarını içeren video çalışması.',
  source: 'institution',
  institutionName: 'Bahçeşehir Koleji',
  teacherName: 'Ayşe Demir',
  markedAsComplete: true,
  markedDate: '2025-10-22',
  targetPath: '/konu-anlatim'
}, {
  id: 103,
  title: 'Endemik Yayınları Fen Test Kitabı',
  subject: 'FEN BİLİMLERİ',
  type: 'physical-book',
  dueDate: '2025-10-24',
  daysLeft: 1,
  status: 'pending',
  description: 'Basınç ve madde konularını içeren fiziki kitap çalışması.',
  source: 'institution',
  institutionName: 'Bahçeşehir Koleji',
  teacherName: 'Zeynep Arslan',
  bookName: 'Endemik Fen Test Kitabı',
  pageRange: 'Test 12-15',
  targetPath: '#'
}, {
  id: 104,
  title: 'Deyimler ve Atasözleri Çalışması',
  subject: 'TÜRKÇE',
  type: 'test',
  dueDate: '2025-10-20',
  daysLeft: -3,
  status: 'completed',
  description: 'Deyimler ve atasözleri konusunu kapsayan test.',
  source: 'institution',
  institutionName: 'Bahçeşehir Koleji',
  teacherName: 'Fatma Yıldız',
  markedAsComplete: true,
  markedDate: '2025-10-19',
  teacherApproved: true,
  approvalDate: '2025-10-20',
  result: {
    description: 'Öğretmen tarafından onaylandı'
  },
  targetPath: '/soru-cozum'
}];
export function Homework() {
  const [mainTab, setMainTab] = useState<'egitim-pilotu' | 'institution'>('egitim-pilotu');
  const [subTab, setSubTab] = useState<'pending' | 'completed'>('pending');
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [assignmentToSchedule, setAssignmentToSchedule] = useState<Assignment | null>(null);
  const {
    gorevEkle
  } = useProgramContext();
  const navigate = useNavigate();
  // Get assignments based on main tab
  const allAssignments = mainTab === 'egitim-pilotu' ? EGITIM_PILOTU_ASSIGNMENTS : INSTITUTION_ASSIGNMENTS;
  // Filter by sub tab (pending includes waiting-approval)
  const filteredAssignments = allAssignments.filter(hw => {
    if (subTab === 'pending') {
      return hw.status === 'pending' || hw.status === 'waiting-approval';
    }
    return hw.status === 'completed';
  });
  const handleStartAssignment = (assignment: Assignment) => {
    if (assignment.type === 'physical-book') {
      // Fiziki kitap için özel modal veya bilgi gösterilebilir
      return;
    }
    navigate(assignment.targetPath);
  };
  const handleMarkAsComplete = (assignmentId: number) => {
    // Bu fonksiyon gerçek uygulamada API çağrısı yapacak
    console.log('Ödev tamamlandı olarak işaretlendi:', assignmentId);
    // Mock: State güncellemesi yapılabilir
  };
  const getDaysLeftText = (days: number) => {
    if (days < 0) return 'Süre Doldu';
    if (days === 0) return 'Bugün Son Gün!';
    if (days === 1) return 'Yarın Son Gün!';
    return `${days} gün kaldı`;
  };
  const getDaysLeftColor = (days: number) => {
    if (days <= 1) return 'bg-red-500/20 text-red-400';
    if (days <= 3) return 'bg-orange-500/20 text-orange-400';
    return 'bg-yellow-500/20 text-yellow-400';
  };
  const getTypeIcon = (type: Assignment['type']) => {
    switch (type) {
      case 'test':
        return BookOpen;
      case 'video':
        return Video;
      case 'physical-book':
        return Book;
      default:
        return BookOpen;
    }
  };
  const handleAddToSchedule = (data: {
    title: string;
    subject: string;
    type: string;
    day: string;
    startTime: string;
    endTime: string;
    notes?: string;
  }) => {
    if (!assignmentToSchedule) return;
    gorevEkle({
      dersAdi: assignmentToSchedule.subject,
      videoAdi: data.title,
      konuAdi: assignmentToSchedule.title,
      gun: data.day,
      saatAraligi: `${data.startTime}-${data.endTime}`,
      notlar: data.notes || assignmentToSchedule.description,
      tamamlandi: false,
      etkinlikTipi: data.type as any
    });
    setScheduleModalOpen(false);
    setAssignmentToSchedule(null);
  };
  const AssignmentCard = ({
    assignment
  }: {
    assignment: Assignment;
  }) => {
    const gradientClass = DERS_RENKLERI[assignment.subject] || 'from-gray-600 to-gray-800';
    const Icon = getTypeIcon(assignment.type);
    const isPending = assignment.status === 'pending';
    const isWaitingApproval = assignment.status === 'waiting-approval';
    const isCompleted = assignment.status === 'completed';
    const isPhysicalBook = assignment.type === 'physical-book';
    return <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-2066">
        <div className="bg-black/90 p-5 rounded-xl space-y-4" data-id="element-2067">
          {/* Header Section */}
          <div className="flex justify-between items-start" data-id="element-2068">
            <div className="flex-1 space-y-2" data-id="element-2069">
              <div className="flex items-center gap-2 flex-wrap" data-id="element-2070">
                <div className={`p-1.5 rounded-md bg-gradient-to-r ${gradientClass}`} data-id="element-2071">
                  <Icon className="w-4 h-4 text-white" data-id="element-2072" />
                </div>
                <span className="text-sm font-medium text-gray-300" data-id="element-2073">
                  {assignment.subject}
                </span>
                {assignment.source === 'institution' && <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-400" data-id="element-2074">
                    {assignment.institutionName}
                  </span>}
              </div>
              <h3 className="text-lg font-bold text-white leading-tight" data-id="element-2075">
                {assignment.title}
              </h3>
              <p className="text-sm text-gray-400" data-id="element-2076">{assignment.description}</p>
              {/* Fiziki kitap bilgisi */}
              {isPhysicalBook && <div className="flex items-center gap-2 text-sm text-blue-400" data-id="element-2077">
                  <Book className="w-4 h-4" data-id="element-2078" />
                  <span data-id="element-2079">
                    {assignment.bookName} - {assignment.pageRange}
                  </span>
                </div>}
              {/* Kurum ödevi - Öğretmen bilgisi */}
              {assignment.source === 'institution' && assignment.teacherName && <p className="text-xs text-gray-500" data-id="element-2080">
                    Öğretmen: {assignment.teacherName}
                  </p>}
            </div>
            {/* Status Badge */}
            {isPending && <div className={`px-3 py-1 text-xs font-medium rounded-full flex items-center gap-1.5 ${getDaysLeftColor(assignment.daysLeft)}`} data-id="element-2081">
                <Clock className="w-3 h-3" data-id="element-2082" />
                {getDaysLeftText(assignment.daysLeft)}
              </div>}
            {isWaitingApproval && <div className="px-3 py-1 text-xs font-medium rounded-full flex items-center gap-1.5 bg-blue-500/20 text-blue-400" data-id="element-2083">
                <AlertCircle className="w-3 h-3" data-id="element-2084" />
                Onay Bekliyor
              </div>}
            {isCompleted && <div className="px-3 py-1 text-xs font-medium rounded-full flex items-center gap-1.5 bg-green-500/20 text-green-400" data-id="element-2085">
                <CheckCircle className="w-3 h-3" data-id="element-2086" />
                Tamamlandı
              </div>}
          </div>
          {/* Footer Section */}
          <div className="border-t border-gray-800 pt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-2087">
            {/* Left: Date or Result */}
            {isCompleted && assignment.result ? <div className="flex items-center gap-2" data-id="element-2088">
                <BarChart2 className="w-5 h-5 text-purple-400" data-id="element-2089" />
                <span className="text-lg font-bold text-white" data-id="element-2090">
                  {assignment.result.description}
                </span>
              </div> : <div className="flex items-center gap-2 text-sm text-gray-400" data-id="element-2091">
                <Calendar className="w-4 h-4" data-id="element-2092" />
                <span data-id="element-2093">
                  Son Teslim:{' '}
                  {new Date(assignment.dueDate).toLocaleDateString('tr-TR', {
                day: 'numeric',
                month: 'long'
              })}
                </span>
              </div>}
            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2 w-full sm:w-auto" data-id="element-2094">
              {/* Programa Ekle (sadece pending için) */}
              {isPending && <button onClick={e => {
              e.stopPropagation();
              setAssignmentToSchedule(assignment);
              setScheduleModalOpen(true);
            }} className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors" data-id="element-2095">
                  <Plus className="w-4 h-4" data-id="element-2096" />
                  <span className="hidden sm:inline" data-id="element-2097">Programa Ekle</span>
                </button>}
              {/* Ana Aksiyon Butonu */}
              {isPending && <>
                  {isPhysicalBook ? <button onClick={() => handleMarkAsComplete(assignment.id)} className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity" data-id="element-2098">
                      <Check className="w-4 h-4" data-id="element-2099" />
                      Tamamladım
                    </button> : <button onClick={() => handleStartAssignment(assignment)} className="flex-1 sm:flex-initial px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity" data-id="element-2100">
                      Ödeve Başla
                    </button>}
                </>}
              {isWaitingApproval && <div className="flex-1 sm:flex-initial px-5 py-2.5 text-center text-sm text-blue-400 bg-blue-500/10 rounded-lg" data-id="element-2101">
                  {assignment.markedDate && <span data-id="element-2102">
                      {new Date(assignment.markedDate).toLocaleDateString('tr-TR')}{' '}
                      tarihinde işaretlendi
                    </span>}
                </div>}
              {isCompleted && <button onClick={() => handleStartAssignment(assignment)} className="flex-1 sm:flex-initial px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity" data-id="element-2103">
                  {isPhysicalBook ? 'Detayları Gör' : 'Sonucu İncele'}
                </button>}
            </div>
          </div>
        </div>
      </div>;
  };
  return <div className="space-y-6" data-id="element-2104">
      {/* Page Header */}
      <div data-id="element-2105">
        <h1 className="text-3xl font-bold text-white" data-id="element-2106">Ödevlerim</h1>
        <p className="text-gray-400 mt-2 max-w-2xl" data-id="element-2107">
          {mainTab === 'egitim-pilotu' ? 'Eğitim Pilotu tarafından sana özel olarak atanan haftalık tekrar testlerini, konu anlatım videolarını ve fiziki test kitabı çalışmalarını burada bulabilirsin.' : 'Kurumun ve öğretmenlerinin sana verdiği ödevleri burada takip edebilirsin.'}
        </p>
      </div>

      {/* Main Tabs (Kaynak Seçimi) */}
      <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 max-w-xl" data-id="element-2108">
        <div className="bg-black/90 rounded-xl p-1" data-id="element-2109">
          <div className="flex" data-id="element-2110">
            <button onClick={() => setMainTab('egitim-pilotu')} className={`flex-1 py-3 px-4 rounded-lg text-center font-medium transition-all ${mainTab === 'egitim-pilotu' ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg' : 'text-gray-400 hover:bg-white/5'}`} data-id="element-2111">
              Eğitim Pilotu Ödevleri
            </button>
            <button onClick={() => setMainTab('institution')} className={`flex-1 py-3 px-4 rounded-lg text-center font-medium transition-all ${mainTab === 'institution' ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg' : 'text-gray-400 hover:bg-white/5'}`} data-id="element-2112">
              Kurum Ödevleri
            </button>
          </div>
        </div>
      </div>

      {/* Sub Tabs (Durum Seçimi) */}
      <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 max-w-md" data-id="element-2113">
        <div className="bg-black/90 rounded-xl p-1" data-id="element-2114">
          <div className="flex" data-id="element-2115">
            <button onClick={() => setSubTab('pending')} className={`flex-1 py-3 px-4 rounded-lg text-center font-medium transition-all ${subTab === 'pending' ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg' : 'text-gray-400 hover:bg-white/5'}`} data-id="element-2116">
              Yapılacaklar
            </button>
            <button onClick={() => setSubTab('completed')} className={`flex-1 py-3 px-4 rounded-lg text-center font-medium transition-all ${subTab === 'completed' ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg' : 'text-gray-400 hover:bg-white/5'}`} data-id="element-2117">
              Tamamlananlar
            </button>
          </div>
        </div>
      </div>

      {/* Assignments List */}
      <div className="space-y-4" data-id="element-2118">
        {filteredAssignments.length > 0 ? filteredAssignments.map(hw => <AssignmentCard key={hw.id} assignment={hw} data-id="element-2119" />) : <div className="text-center py-16 bg-black/40 rounded-xl" data-id="element-2120">
            <div className="flex flex-col items-center gap-3" data-id="element-2121">
              <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center" data-id="element-2122">
                <CheckCircle className="w-8 h-8 text-purple-400" data-id="element-2123" />
              </div>
              <h3 className="text-xl font-semibold text-white" data-id="element-2124">
                Harika Gidiyorsun!
              </h3>
              <p className="text-gray-400 max-w-md" data-id="element-2125">
                {subTab === 'pending' ? mainTab === 'egitim-pilotu' ? 'Şu an Eğitim Pilotu tarafından bekleyen ödevin yok. Yeni ödevler yakında gelecek.' : 'Şu an kurum tarafından bekleyen ödevin yok.' : mainTab === 'egitim-pilotu' ? 'Henüz Eğitim Pilotu ödevlerinden tamamlanmış bir ödevin bulunmuyor.' : 'Henüz kurum ödevlerinden tamamlanmış bir ödevin bulunmuyor.'}
              </p>
            </div>
          </div>}
      </div>

      {/* Schedule Modal */}
      {scheduleModalOpen && assignmentToSchedule && <ScheduleModal initialData={{
      title: assignmentToSchedule.title,
      type: 'homework',
      subject: assignmentToSchedule.subject,
      homeworkId: assignmentToSchedule.id,
      description: assignmentToSchedule.description
    }} onClose={() => {
      setScheduleModalOpen(false);
      setAssignmentToSchedule(null);
    }} onSave={handleAddToSchedule} data-id="element-2126" />}
    </div>;
}